import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  CURSOR_QUERY_PARAM,
  DataTable,
  LIMIT_QUERY_PARAM,
} from '@/components/ui/data-table';
import { TableTitle } from '@/components/ui/table-title';
import { AdminUser, SeekPage } from '@activepieces/shared';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { useAdminUsersTable } from '@/features/internal-admin/lib/admin-users-table';

const AdminUsersPage = () => {
  const { columns, filters } = useAdminUsersTable();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const queryParams = {
    cursor: searchParams.get(CURSOR_QUERY_PARAM) || undefined,
    limit: Number(searchParams.get(LIMIT_QUERY_PARAM)) || 10,
    search: searchParams.get('email') ?? undefined,
    externalId: searchParams.get('externalId') ?? undefined,
    externalCustomerId: searchParams.get('externalCustomerId') ?? undefined,
    isInternalKizeo:
      searchParams.getAll('isInternalKizeo').length === 1
        ? searchParams.getAll('isInternalKizeo')[0] === 'true'
        : undefined,
    status:
      searchParams.getAll('status').length > 0
        ? searchParams.getAll('status')
        : undefined,
  };

  const { data, isLoading, isError } = useQuery<SeekPage<AdminUser>, Error>({
    queryKey: ['admin-users', queryParams],
    queryFn: () => adminApi.listUsers(queryParams),
  });

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('Failed to load users')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      <TableTitle>{t('Users')}</TableTitle>
      <DataTable
        columns={columns}
        page={data}
        isLoading={isLoading}
        filters={filters}
        hiddenColumns={['isInternalKizeo']}
        onRowClick={(row, newWindow) => {
          const url = `/internal-admin/users/${row.id}`;
          if (newWindow) {
            window.open(url, '_blank');
          } else {
            navigate(url);
          }
        }}
      />
    </div>
  );
};

export default AdminUsersPage;
